/**
 * AuthContext — replaces AngularJS AuthService + AuthInterceptor factory.
 *
 * Lifecycle (mirrors original loadCredentials() run-block):
 *   1. On mount: read token from localStorage.
 *   2. If token present, POST /validate to confirm it's still good.
 *   3. On success: mark isAuthenticated, save driver profile.
 *   4. On network failure: stay authenticated (offline-first, same as original).
 *   5. On 401 from validate: destroyCredentials().
 *
 * login() → POST /authenticate → stores token + driver.
 * logout() → destroyCredentials().
 *
 * axios default header is set here so every subsequent request carries
 * Authorization: Bearer <token> — same as the original $http interceptor.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { BASE_URL, TOKEN_KEY } from '@/env';
import type { Driver } from '@/types';

interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  driver: Driver | null;
  /** true while the initial /validate call is in flight */
  initialising: boolean;
}

interface AuthContextValue extends AuthState {
  login: (licence: string, pincode: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    token: null,
    driver: null,
    initialising: true,
  });

  // Keep a ref so setAxiosHeader() can be called without re-renders
  const tokenRef = useRef<string | null>(null);

  const setAxiosHeader = useCallback((token: string | null) => {
    tokenRef.current = token;
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, []);

  const destroyCredentials = useCallback(() => {
    window.localStorage.removeItem(TOKEN_KEY);
    setAxiosHeader(null);
    setState({ isAuthenticated: false, token: null, driver: null, initialising: false });
  }, [setAxiosHeader]);

  // Validate stored token on mount (mirrors original loadCredentials())
  useEffect(() => {
    const stored = window.localStorage.getItem(TOKEN_KEY);
    if (!stored) {
      setState(s => ({ ...s, initialising: false }));
      return;
    }

    // Optimistically set header so the validate request itself is authorized
    setAxiosHeader(stored);

    axios
      .post<{ success: boolean; driver: Driver }>(`${BASE_URL}validate`)
      .then(({ data }) => {
        if (data.success) {
          window.localStorage.setItem('driver', JSON.stringify(data.driver));
          setState({
            isAuthenticated: true,
            token: stored,
            driver: data.driver,
            initialising: false,
          });
        } else {
          destroyCredentials();
        }
      })
      .catch(() => {
        // Network failure: keep the user logged in (offline-first parity)
        const savedDriver = window.localStorage.getItem('driver');
        setState({
          isAuthenticated: true,
          token: stored,
          driver: savedDriver ? (JSON.parse(savedDriver) as Driver) : null,
          initialising: false,
        });
      });
  }, [setAxiosHeader, destroyCredentials]);

  // Intercept 401 globally — mirrors AuthInterceptor responseError
  useEffect(() => {
    const id = axios.interceptors.response.use(
      r => r,
      error => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          destroyCredentials();
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, [destroyCredentials]);

  const login = useCallback(
    async (licence: string, pincode: string): Promise<void> => {
      const { data } = await axios.post<{
        success: boolean;
        token: string;
        driver: Driver;
        message?: string;
      }>(`${BASE_URL}authenticate`, { licence, pincode });

      if (!data.success) throw new Error(data.message ?? 'Login failed.');

      window.localStorage.setItem(TOKEN_KEY, data.token);
      window.localStorage.setItem('driver', JSON.stringify(data.driver));
      setAxiosHeader(data.token);

      setState({
        isAuthenticated: true,
        token: data.token,
        driver: data.driver,
        initialising: false,
      });
    },
    [setAxiosHeader],
  );

  const logout = useCallback(() => {
    destroyCredentials();
  }, [destroyCredentials]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout }),
    [state, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
